import Sheet from 'shared/Sheet'
import TextField from 'shared/Forms/TextField'
import TextInput from 'shared/Inputs/TextInput'
import { PropsWithChildren, useMemo, useState } from 'react'
import { IProjectStage, IProjectType } from '../../projectTypes.types'
import TwoListSelect from 'shared/TwoListSelect'
import searchUtils from 'utils/search'
import textUtils from 'utils/text.utils'
import CreateProjectStageModal from '../CreateProjectStageModal'
import { useProjectStagesState } from '../../hooks/useProjectStagesState'
import ProjectStageModal from '../ProjectStageModal'
import durationUtils from '../../utils/durationUtils'
import { useAllCheckpointsState } from 'screens/Admin/ProjectCheckpoints/hooks/useAllCheckpointsState'

interface IProps {
  projectType: Pick<IProjectType, 'name' | 'projectStages'>
  onChange: (val: any, name: string) => void
}

const ProjectTypeSheet = (props: PropsWithChildren<IProps>) => {
  const [isOpenCreateStageModal, setIsOpenCreateStageModal] = useState(false)
  const [openedProjectStage, setOpenedProjectStage] = useState<IProjectStage>()

  const { projectStagesState, projectStagesMap } = useProjectStagesState()
  const { checkpointsMap } = useAllCheckpointsState()

  const selectedStagesOptions = useMemo(() => {
    return props.projectType.projectStages
  }, [props.projectType.projectStages])

  const stagesOptions = projectStagesState.items.map((ps) => ({ title: ps.name, value: ps.id }))

  const handleClickAdd = () => {
    setIsOpenCreateStageModal(true)
  }

  const handleCloseCreateModal = () => {
    setIsOpenCreateStageModal(false)
  }

  const handleClickMoreDetails = (projectStageId: IProjectStage['id']) => {
    setOpenedProjectStage(projectStagesMap[projectStageId])
  }

  const handleCloseStageModal = () => {
    setOpenedProjectStage(undefined)
  }

  const handleSelect = (values: IProjectStage['id'][]) => {
    const addToSelected = values.map((v) => ({ projectStageId: v, orderingIndex: 0 }))
    props.onChange(
      [...addToSelected, ...props.projectType.projectStages].map((value, i) => ({
        ...value,
        orderingIndex: i + 1,
      })),
      'projectStages'
    )
  }

  const handleUnSelect = (values: IProjectStage['id'][]) => {
    const filtered = props.projectType.projectStages?.filter((ps) => !values.includes(ps.projectStageId))
    props.onChange(filtered, 'projectStages')
  }

  const handleChangeOrder = (orderedIds: IProjectStage['id'][]) => {
    props.onChange(
      orderedIds.map((id, i) => ({ projectStageId: id, orderingIndex: i + 1 })),
      'projectStages'
    )
  }

  const getStageDuration = (stageId: IProjectStage['id']) => {
    const stage = projectStagesMap[stageId]

    return durationUtils.getProjectStageDuration(stage, checkpointsMap)
  }

  const duration = durationUtils.getProjectTypeDuration(props.projectType, projectStagesMap, checkpointsMap)

  const durationText = `${duration} ${textUtils.declineWordDependingOnCount(duration, ['дней', 'день', 'дня'])}`

  return (
    <Sheet>
      {isOpenCreateStageModal && <CreateProjectStageModal onClose={handleCloseCreateModal} />}
      {!!openedProjectStage && <ProjectStageModal onClose={handleCloseStageModal} projectStage={openedProjectStage} />}
      <div className='flex-lines'>
        <div className='grid-form-2'>
          <TextField header='Наименование'>
            <TextInput value={props.projectType.name} onChange={props.onChange} name='name' placeholder='Учебный' />
          </TextField>
          <TextField header='Длительность'>
            <TextInput value={durationText} onChange={() => {}} name='name' disabled />
          </TextField>
        </div>
        <div className='flex-lines'>
          <TwoListSelect
            options={stagesOptions}
            selected={selectedStagesOptions.map((ps) => ps.projectStageId)}
            titleLeft={'Этапы проекта'}
            titleRight={'Все этапы проекта'}
            onClickAdd={handleClickAdd}
            onSelect={handleSelect}
            onUnselect={handleUnSelect}
            onChangeOrder={handleChangeOrder}
            onClickMoreDetails={handleClickMoreDetails}
            optionTitle={(option) => (
              <div>
                <p>{searchUtils.renderSearchResult(option.search)}</p>
                <p className='text--body-s text--secondary '>
                  {getStageDuration(option.value)}{' '}
                  {textUtils.declineWordDependingOnCount(getStageDuration(option.value), ['дней', 'день', 'дня'])}
                </p>
              </div>
            )}
          />
        </div>
      </div>
      <div className='form-footer'>{props.children}</div>
    </Sheet>
  )
}

export default ProjectTypeSheet
